import { USDC_DECIMALS } from "@breakout/sdk"
import { TRADING } from "@breakout/shared/constants"
import { Button } from "@breakout/ui/components/Button"
import { formatShares, formatUsd } from "@breakout/ui/utils"
import { TrendingDown, TrendingUp } from "lucide-react"
import { memo } from "react"
import { formatUnits } from "viem"

import { useCountdown } from "../../hooks/common/useCountdown"
import type { OutcomeBucket } from "../../lib/types"
import { SimpleAvatar } from "../ui/SimpleAvatar"

export type TradeMode = "buy" | "sell"

interface TradingPanelProps {
	buckets: OutcomeBucket[]
	selectedBucketId: string | null
	onSelectBucket: (bucket: OutcomeBucket) => void
	tradeMode: TradeMode
	onSetTradeMode: (mode: TradeMode) => void
	amount: number
	onSetAmount: (amount: number) => void
	userShares?: Record<string, number>
	isAuthenticated: boolean
	isSubmitting: boolean
	statusLabel?: string
	onConfirm: () => void
	onLogin?: () => void
	isMarketEnded?: boolean
	/** Market open time in milliseconds, null if already open */
	openTimeMs?: number | null
	/** Estimated shares to receive (buy mode), null if quote unavailable */
	estimatedShares: number | null
	/** Whether this is a Fall from Grace market (all outcomes show down arrow) */
	isFallFromGrace?: boolean
	/** Whether to show "Add cash" button due to insufficient funds */
	showAddCash?: boolean
	/** Handler to open Privy fund wallet modal */
	onAddCash?: () => void
	/** User's raw USDC balance in 6 decimals (null when not loaded) */
	userBalanceRaw: bigint | null
}

export const TradingPanel = memo(function TradingPanel({
	buckets,
	selectedBucketId,
	onSelectBucket,
	tradeMode,
	onSetTradeMode,
	amount,
	onSetAmount,
	userShares = {},
	isAuthenticated,
	isSubmitting,
	statusLabel,
	onConfirm,
	onLogin,
	isMarketEnded = false,
	openTimeMs,
	estimatedShares,
	isFallFromGrace = false,
	showAddCash = false,
	onAddCash,
	userBalanceRaw,
}: TradingPanelProps) {
	// Use countdown hook for "Opens in" display
	const { timeRemaining: timeUntilOpen, isEnded: isOpen } = useCountdown(openTimeMs ?? null)
	const isMarketOpen = openTimeMs == null || isOpen
	const isTradingDisabled = !isMarketOpen || isMarketEnded
	const selectedBucket = buckets.find(b => b.id === selectedBucketId) ?? buckets[0]
	const numAmount = amount
	const selectedShares = selectedBucket ? (userShares[selectedBucket.id] ?? 0) : 0

	// Buy mode: payout if you win = shares (since each share pays $1 on winning outcome)
	// Sell mode: proceeds = shares * current price
	const buyPayout =
		estimatedShares ??
		(selectedBucket && selectedBucket.probability > 0 ? numAmount / selectedBucket.probability : 0)
	const sellProceeds = numAmount * (selectedBucket?.price ?? 0)

	// Check if buy amount is below minimum
	const isBelowMinimum = tradeMode === "buy" && numAmount > 0 && numAmount < TRADING.MIN_BUY_AMOUNT

	// Check if user has insufficient funds for buy (compare amount to balance in human-readable form)
	// Only check for authenticated users - logged out users should see the normal buy button that triggers login
	const userBalanceHuman =
		userBalanceRaw != null ? Number(formatUnits(userBalanceRaw, USDC_DECIMALS)) : 0
	const isInsufficientFunds =
		isAuthenticated && tradeMode === "buy" && numAmount > 0 && numAmount > userBalanceHuman

	// Check if user has insufficient shares for sell
	const isInsufficientShares = tradeMode === "sell" && numAmount > 0 && numAmount > selectedShares

	const canSubmit =
		Boolean(selectedBucket) &&
		numAmount > 0 &&
		!isBelowMinimum &&
		!isInsufficientFunds &&
		!isInsufficientShares

	return (
		<div className="space-y-5">
			<TradeModeToggle mode={tradeMode} onModeChange={onSetTradeMode} />

			<OutcomeSelector
				buckets={buckets}
				isFallFromGrace={isFallFromGrace}
				onSelectBucket={onSelectBucket}
				selectedBucketId={selectedBucketId}
			/>

			<AmountInput
				amount={amount}
				buyPayout={tradeMode === "buy" ? buyPayout : null}
				mode={tradeMode}
				onAmountChange={onSetAmount}
				selectedShares={selectedShares}
				sellProceeds={tradeMode === "sell" ? sellProceeds : null}
				showPayout={numAmount > 0 && !isMarketEnded}
				userBalanceRaw={userBalanceRaw}
			/>

			{/* Show shares owned in sell mode */}
			{tradeMode === "sell" && selectedShares > 0 && (
				<p className="text-center text-breakout-brand text-sm">
					You own <span className="font-semibold">{formatShares(selectedShares)}</span> shares
				</p>
			)}

			{/* Action button - Shows "Add cash" if insufficient funds or zero balance, otherwise buy/sell */}
			{(showAddCash || isInsufficientFunds) && tradeMode === "buy" && onAddCash ? (
				<Button className="w-full" onClick={onAddCash} size="lg" variant="primary">
					Add cash
				</Button>
			) : (
				<Button
					className="w-full"
					disabled={isAuthenticated && (isTradingDisabled || !canSubmit || isSubmitting)}
					loading={isSubmitting}
					onClick={!isAuthenticated && onLogin ? onLogin : onConfirm}
					size="lg"
					variant={
						!isMarketOpen
							? "secondary"
							: isMarketEnded
								? "secondary"
								: tradeMode === "buy"
									? "primary"
									: "danger"
					}
				>
					{!isMarketOpen
						? "Opens soon"
						: isMarketEnded
							? "Ended"
							: isSubmitting && statusLabel
								? statusLabel
								: isSubmitting
									? "Processing…"
									: isBelowMinimum
										? "Minimum $1"
										: isInsufficientShares
											? "Insufficient Shares"
											: tradeMode === "buy"
												? `Buy ${selectedBucket?.label ?? "shares"}`
												: `Sell ${selectedBucket?.label ?? "shares"}`}
				</Button>
			)}

			{/* Market not open banner */}
			{!isMarketOpen && timeUntilOpen && (
				<div className="rounded-xl bg-amber-50 p-4 text-center dark:bg-amber-900/30">
					<p className="font-medium text-amber-700 text-sm dark:text-amber-300">Market opens in</p>
					<p className="mt-1 font-bold text-amber-900 text-lg tabular-nums dark:text-amber-100">
						{timeUntilOpen}
					</p>
				</div>
			)}
		</div>
	)
})

// =============================================================================
// Subcomponents (single-use, colocated)
// =============================================================================

interface TradeModeToggleProps {
	mode: TradeMode
	onModeChange: (mode: TradeMode) => void
}

const TradeModeToggle = memo(function TradeModeToggle({
	mode,
	onModeChange,
}: TradeModeToggleProps) {
	return (
		<fieldset className="flex rounded-xl border-0 bg-bg-tertiary p-1 ring-1 ring-border-subtle">
			<legend className="sr-only">Trade mode</legend>
			<button
				aria-pressed={mode === "buy"}
				className={`min-h-[44px] flex-1 cursor-pointer rounded-lg py-2.5 font-semibold text-base transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand focus-visible:ring-offset-2 active:scale-[0.98] ${
					mode === "buy"
						? "bg-bg-card text-text-primary shadow-sm ring-1 ring-border-subtle"
						: "text-text-muted hover:bg-bg-secondary/50 hover:text-text-secondary"
				}`}
				onClick={() => onModeChange("buy")}
				type="button"
			>
				Buy
			</button>
			<button
				aria-pressed={mode === "sell"}
				className={`min-h-[44px] flex-1 cursor-pointer rounded-lg py-2.5 font-semibold text-base transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand focus-visible:ring-offset-2 active:scale-[0.98] ${
					mode === "sell"
						? "bg-bg-card text-text-primary shadow-sm ring-1 ring-border-subtle"
						: "text-text-muted hover:bg-bg-secondary/50 hover:text-text-secondary"
				}`}
				onClick={() => onModeChange("sell")}
				type="button"
			>
				Sell
			</button>
		</fieldset>
	)
})

interface OutcomeSelectorProps {
	buckets: OutcomeBucket[]
	selectedBucketId: string | null
	onSelectBucket: (bucket: OutcomeBucket) => void
	/** Whether this is a Fall from Grace market (all outcomes show down arrow) */
	isFallFromGrace?: boolean
}

const OutcomeSelector = memo(function OutcomeSelector({
	buckets,
	selectedBucketId,
	onSelectBucket,
	isFallFromGrace = false,
}: OutcomeSelectorProps) {
	return (
		<fieldset className="space-y-2 border-0">
			<legend className="mb-2 text-sm text-text-secondary">Choose outcome</legend>
			<div className="grid gap-2">
				{buckets.map(bucket => {
					// For FFG markets, all outcomes are "down" since it's betting on who falls most
					const isUp = !isFallFromGrace && bucket.direction === "up"
					const isDown = isFallFromGrace || bucket.direction === "down"
					const isSelected = bucket.id === selectedBucketId
					const priceCents = Math.round(bucket.probability * 100)

					return (
						<button
							aria-pressed={isSelected}
							className={`flex min-h-[52px] items-center justify-between rounded-xl border-2 px-3 py-2.5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-[0.98] sm:px-4 sm:py-3 ${
								isSelected
									? isUp
										? "border-green-500 bg-green-500/10 focus-visible:ring-green-500"
										: isDown
											? "border-red-500 bg-red-500/10 focus-visible:ring-red-500"
											: "border-text-muted bg-bg-tertiary focus-visible:ring-text-muted"
									: "border-border bg-bg-card hover:border-border-highlight hover:bg-bg-secondary/30 focus-visible:ring-border-highlight"
							}`}
							key={bucket.id}
							onClick={() => onSelectBucket(bucket)}
							type="button"
						>
							<div className="flex items-center gap-3">
								{/* Avatar with arrow overlay - rounded square */}
								<div className="relative size-8 shrink-0">
									{bucket.avatarUrl ? (
										<>
											<SimpleAvatar
												alt={bucket.label}
												className="size-8"
												size="sm"
												src={bucket.avatarUrl}
												variant="square"
											/>
											{/* Arrow overlay */}
											<div
												className={`absolute -right-1 -bottom-1 flex size-4 items-center justify-center rounded-full ${
													isUp ? "bg-green-500" : isDown ? "bg-red-500" : "bg-gray-500"
												}`}
											>
												{isUp ? (
													<TrendingUp aria-hidden="true" className="size-2.5 text-white" />
												) : isDown ? (
													<TrendingDown aria-hidden="true" className="size-2.5 text-white" />
												) : (
													<span className="font-bold text-[8px] text-white">~</span>
												)}
											</div>
										</>
									) : (
										<div
											className={`flex size-8 items-center justify-center rounded-lg ${
												isUp ? "bg-green-500/20" : isDown ? "bg-red-500/20" : "bg-bg-tertiary"
											}`}
										>
											{isUp ? (
												<TrendingUp aria-hidden="true" className="size-4 text-green-600" />
											) : isDown ? (
												<TrendingDown aria-hidden="true" className="size-4 text-red-600" />
											) : (
												<span className="font-semibold text-slate-600 text-xs">~</span>
											)}
										</div>
									)}
								</div>
								<span className="font-medium text-text-primary">{bucket.label}</span>
							</div>
							<div className="text-right">
								<span
									className={`font-bold text-lg tabular-nums ${
										isUp ? "text-green-500" : isDown ? "text-red-500" : "text-text-muted"
									}`}
								>
									{priceCents}c
								</span>
							</div>
						</button>
					)
				})}
			</div>
		</fieldset>
	)
})

const BUY_PERCENTAGES = [25, 50, 75, 100] as const
const SELL_PERCENTAGES = [25, 50, 75, 100] as const

interface AmountInputProps {
	mode: TradeMode
	amount: number
	onAmountChange: (amount: number) => void
	selectedShares: number
	showPayout?: boolean
	buyPayout?: number | null
	sellProceeds?: number | null
	/** User's raw USDC balance in 6 decimals (null when not loaded) */
	userBalanceRaw: bigint | null
}

const AmountInput = memo(function AmountInput({
	mode,
	amount,
	onAmountChange,
	selectedShares,
	showPayout = false,
	buyPayout,
	sellProceeds,
	userBalanceRaw,
}: AmountInputProps) {
	return (
		<div>
			<label className="text-sm text-text-secondary" htmlFor="panel-amount">
				{mode === "buy" ? "Amount (USDC)" : "Shares to sell"}
			</label>
			<div className="relative mt-2">
				<span className="absolute top-1/2 left-4 -translate-y-1/2 font-semibold text-text-muted text-xl">
					{mode === "buy" ? "$" : "#"}
				</span>
				<input
					autoComplete="off"
					className="w-full rounded-xl border border-border bg-bg-card py-3 pr-4 pl-10 text-right font-semibold text-text-primary text-xl transition focus-visible:border-breakout-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand/20"
					id="panel-amount"
					min="0"
					onChange={e => onAmountChange(e.target.valueAsNumber || 0)}
					placeholder="0"
					step="0.01"
					type="number"
					value={amount || ""}
				/>
			</div>

			{/* Max Payout / Estimated proceeds - prominent centered display */}
			{showPayout && (
				<div className="mt-4 text-center">
					{mode === "buy" && buyPayout != null ? (
						<div className="rounded-xl bg-green-500/10 px-4 py-3">
							<p className="font-medium text-green-600 text-sm dark:text-green-400">Max Payout</p>
							<p className="font-bold text-2xl text-green-500 tabular-nums lg:text-3xl">
								{formatUsd(buyPayout)}
							</p>
						</div>
					) : mode === "sell" && sellProceeds != null ? (
						<div className="rounded-xl bg-green-500/10 px-4 py-3">
							<p className="font-medium text-green-600 text-sm dark:text-green-400">Proceeds</p>
							<p className="font-bold text-2xl text-green-500 tabular-nums lg:text-3xl">
								+{formatUsd(sellProceeds)}
							</p>
						</div>
					) : null}
				</div>
			)}

			{/* Quick amounts */}
			<div className="mt-3 flex gap-2">
				{mode === "buy"
					? BUY_PERCENTAGES.map(pct => {
							// Calculate amount in bigint to avoid JS floating point issues
							// Then convert to number for the amount state
							const calculatedAmountRaw =
								userBalanceRaw != null
									? pct === 100
										? userBalanceRaw // Full balance, no math needed
										: (userBalanceRaw * BigInt(pct)) / 100n // Bigint division floors automatically
									: 0n
							// Convert to human-readable number (USDC has 6 decimals)
							const calculatedAmount =
								userBalanceRaw != null ? Number(formatUnits(calculatedAmountRaw, USDC_DECIMALS)) : 0
							const isDisabled = userBalanceRaw == null || userBalanceRaw === 0n
							return (
								<button
									aria-label={`Set amount to ${pct}% of balance`}
									className="min-h-[44px] flex-1 cursor-pointer rounded-lg border border-border bg-bg-card py-2.5 text-sm text-text-secondary transition-all hover:border-border-highlight hover:bg-bg-card-hover hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand active:scale-[0.96] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100 sm:py-3 sm:text-base"
									disabled={isDisabled}
									key={pct}
									onClick={() => onAmountChange(calculatedAmount)}
									type="button"
								>
									{pct === 100 ? "Max" : `${pct}%`}
								</button>
							)
						})
					: SELL_PERCENTAGES.map(pct => {
							// Use exact amount for 100%, floor for partial percentages
							const sellAmount =
								pct === 100 ? selectedShares : Math.floor((selectedShares * pct) / 100)
							return (
								<button
									aria-label={`Sell ${pct}% of shares`}
									className="min-h-[44px] flex-1 cursor-pointer rounded-lg border border-border bg-bg-card py-2.5 text-sm text-text-secondary transition-all hover:border-border-highlight hover:bg-bg-card-hover hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand active:scale-[0.96] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100 sm:py-3 sm:text-base"
									disabled={selectedShares === 0}
									key={pct}
									onClick={() => onAmountChange(sellAmount)}
									type="button"
								>
									{pct === 100 ? "Max" : `${pct}%`}
								</button>
							)
						})}
			</div>
		</div>
	)
})
